import React, { createContext } from "react";
import { common, createLowlight } from "lowlight";
import { useEditor } from "@tiptap/react";
import { Color } from "@tiptap/extension-color";
import TextStyle from "@tiptap/extension-text-style";
import ListItem from "@tiptap/extension-list-item";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import Image from "@tiptap/extension-image";
import Underline from "@tiptap/extension-underline";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import Highlight from "@tiptap/extension-highlight";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import Gapcursor from "@tiptap/extension-gapcursor";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import TextDirection from "tiptap-text-direction";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Youtube from "@tiptap/extension-youtube";
import { Extension, Node } from "@tiptap/core";

const CustomTextAlign = TextAlign.extend({
  addOptions() {
    return {
      types: [],
      alignments: ["left", "center", "right", "justify"],
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          textAlign: {
            parseHTML: (element) =>
              element.style.textAlign || this.options.defaultAlignment,
            renderHTML: (attributes) => {
              const alignment = attributes.textAlign;
              return { style: `text-align: ${alignment}` };
            },
          },
        },
      },
    ];
  },
});

// Custom FontSize Extension
const FontSize = Extension.create({
  name: "fontSize",
  addOptions() {
    return {
      types: ["textStyle"],
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize?.replace(/['"]+/g, ""),
            renderHTML: (attributes) => {
              if (!attributes.fontSize) {
                return {};
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize:
        (fontSize) =>
        ({ chain }) => {
          return chain().setMark("textStyle", { fontSize }).run();
        },
      unsetFontSize:
        () =>
        ({ chain }) => {
          return chain()
            .setMark("textStyle", { fontSize: null })
            .removeEmptyTextStyle()
            .run();
        },
    };
  },
});

// Custom FontFamily Extension
const FontFamily = Extension.create({
  name: "fontFamily",
  addOptions() {
    return {
      types: ["textStyle"],
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontFamily: {
            default: null,
            parseHTML: (element) => element.style.fontFamily?.replace(/['"]+/g, ""),
            renderHTML: (attributes) => {
              if (!attributes.fontFamily) {
                return {};
              }
              return {
                style: `font-family: ${attributes.fontFamily}`,
              };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontFamily:
        (fontFamily) =>
        ({ chain }) => {
          return chain().setMark("textStyle", { fontFamily }).run();
        },
      unsetFontFamily:
        () =>
        ({ chain }) => {
          return chain()
            .setMark("textStyle", { fontFamily: null })
            .removeEmptyTextStyle()
            .run();
        },
    };
  },
});

// Custom BackgroundColor Extension (different from Highlight)
const BackgroundColor = Extension.create({
  name: "backgroundColor",
  addOptions() {
    return {
      types: ["textStyle"],
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          backgroundColor: {
            default: null,
            parseHTML: (element) => element.style.backgroundColor,
            renderHTML: (attributes) => {
              if (!attributes.backgroundColor) {
                return {};
              }
              return {
                style: `background-color: ${attributes.backgroundColor}`,
              };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setBackgroundColor:
        (backgroundColor) =>
        ({ chain }) => {
          return chain().setMark("textStyle", { backgroundColor }).run();
        },
      unsetBackgroundColor:
        () =>
        ({ chain }) => {
          return chain()
            .setMark("textStyle", { backgroundColor: null })
            .removeEmptyTextStyle()
            .run();
        },
    };
  },
});

// Custom Details Node (HTML <details> tag)
const Details = Node.create({
  name: "details",

  group: "block",

  content: "block+",

  defining: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  parseHTML() {
    return [
      {
        tag: "details",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "details",
      HTMLAttributes,
      ["summary", {}, "Details"],
      ["div", {}, 0],
    ];
  },

  addCommands() {
    return {
      setDetails:
        () =>
        ({ commands }) => {
          return commands.wrapIn(this.name);
        },
      toggleDetails:
        () =>
        ({ commands }) => {
          return commands.toggleWrap(this.name);
        },
      unsetDetails:
        () =>
        ({ commands }) => {
          return commands.lift(this.name);
        },
    };
  },
});

// Custom StyledContainer Node - for beautifully styled content blocks
const StyledContainer = Node.create({
  name: "styledContainer",

  group: "block",

  content: "block+",

  defining: true,

  addAttributes() {
    return {
      variant: {
        default: "info",
        parseHTML: (element) => element.getAttribute("data-variant") || "info",
        renderHTML: (attributes) => {
          return {
            "data-variant": attributes.variant,
          };
        },
      },
    };
  },

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  parseHTML() {
    return [
      {
        tag: "div[data-variant]",
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const variant = node.attrs.variant || "info";

    // Define styling for each variant
    const variantStyles = {
      info: {
        background: "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)",
        border: "2px solid #60A5FA",
        borderRadius: "1rem",
        padding: "1rem",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      },
      success: {
        background: "linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)",
        border: "2px solid #4ADE80",
        borderRadius: "1rem",
        padding: "1rem",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      },
      warning: {
        background: "linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)",
        border: "2px solid #FBBF24",
        borderRadius: "1rem",
        padding: "1rem",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      },
      question: {
        background: "linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)",
        border: "2px solid #818CF8",
        borderRadius: "1rem",
        padding: "1rem",
        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
      },
      highlight: {
        background: "linear-gradient(135deg, #FEF9C3 0%, #FDE047 40%)",
        border: "2px solid #FACC15",
        borderRadius: "0.5rem",
        padding: "0.75rem",
        boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
      },
      gradient: {
        background: "linear-gradient(135deg, #FDF4FF 0%, #FAE8FF 50%, #F3E8FF 100%)",
        border: "2px solid #D8B4FE",
        borderRadius: "1rem",
        padding: "1rem",
        boxShadow: "0 4px 8px rgba(0,0,0,0.12)",
      },
      quote: {
        background: "#F9FAFB",
        borderLeft: "4px solid #6B7280",
        borderRadius: "0.25rem",
        padding: "1rem 1rem 1rem 1.5rem",
        fontStyle: "italic",
      },
      minimal: {
        border: "1px solid #E5E7EB",
        borderRadius: "0.5rem",
        padding: "0.75rem",
      },
    };

    const style = variantStyles[variant] || variantStyles.info;
    const styleString = Object.entries(style)
      .map(([key, value]) => {
        // Convert camelCase to kebab-case
        const kebabKey = key.replace(/([A-Z])/g, "-$1").toLowerCase();
        return `${kebabKey}: ${value}`;
      })
      .join("; ");

    return [
      "div",
      {
        ...HTMLAttributes,
        "data-variant": variant,
        class: `richtext-${variant}`,
        style: styleString,
      },
      0,
    ];
  },

  addCommands() {
    return {
      setStyledContainer:
        (variant = "info") =>
        ({ commands }) => {
          return commands.wrapIn(this.name, { variant });
        },
      toggleStyledContainer:
        (variant = "info") =>
        ({ commands }) => {
          return commands.toggleWrap(this.name, { variant });
        },
      unsetStyledContainer:
        () =>
        ({ commands }) => {
          return commands.lift(this.name);
        },
    };
  },
});

const TiptapContext = createContext({
  editor: null,
});

function TiptapProvider({ children }) {
  const lowlight = createLowlight(common);

  const editor = useEditor({
    extensions: [
      Color.configure({ types: [TextStyle.name, ListItem.name] }),
      TextStyle.configure({ types: [ListItem.name] }),
      StarterKit.configure({
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
      }),
      CustomTextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
      Underline,
      CodeBlockLowlight.configure({
        lowlight,
      }),
      Highlight,
      Document,
      Paragraph,
      Text,
      Gapcursor,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      TextDirection.configure({
        types: ["paragraph", "heading"],
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-primary underline",
        },
      }),
      Placeholder.configure({
        placeholder: "Write here …",
      }),
      // New extensions
      Subscript,
      Superscript,
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      Youtube.configure({
        controls: true,
        nocookie: true,
      }),
      FontSize,
      FontFamily,
      BackgroundColor,
      Details,
      StyledContainer,
    ],
    editorProps: {
      attributes: {
        class: "m-2 focus:outline-none",
        spellcheck: "false",
      },
    },
  });

  return (
    <TiptapContext.Provider
      value={{
        editor,
      }}
    >
      {children}
    </TiptapContext.Provider>
  );
}

export { TiptapProvider, TiptapContext };
